import { GROUP_LIFE_PRODUCT_CODE } from '@/app/config/products.config';
import { integrationsService } from '@/services/integrations';
import type { EmissionControl } from '@/services/integrations/types';
import { useCanManualEmission } from '@/shared/hooks';
import { toaster } from '@/shared/ui/toaster.component';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Ações do pacote de emissão (kit de implantação) do Vida PME: a proposta e a
 * carta comercial assinadas e a planilha das vidas de cada subestipulante, para
 * a área emitir a apólice no i4Pro.
 *
 * - "Baixar kit de implantação": em todo controle PME, em qualquer status —
 *   serve também para conferir depois da emissão.
 * - "Reenviar pacote": reenvia a Operações o aviso com o pacote (envio que
 *   falhou, ou pedido da área). Só Administrador, como na API. O BFF responde na
 *   hora e o resultado aparece no histórico.
 *
 * O BFF lê cada vida no InsureMO, então o download pode levar alguns segundos:
 * o botão mostra o carregamento e o erro vai num toast.
 */
export function useImplantationKitDownload(control?: EmissionControl | null) {
  const queryClient = useQueryClient();
  const { canManualEmission } = useCanManualEmission();
  const isGroupLife = control?.productCode === GROUP_LIFE_PRODUCT_CODE;

  const mutation = useMutation({
    mutationFn: (policyId: string) =>
      integrationsService.downloadImplantationKit(policyId),
    onSuccess: response => {
      if (response.success) return;
      toaster.create({
        type: 'error',
        title: 'Kit de implantação',
        description:
          response.error ?? 'Não foi possível baixar o kit de implantação.',
      });
    },
  });

  const resendMutation = useMutation({
    mutationFn: (policyId: string) =>
      integrationsService.resendImplantationKit(policyId),
    onSuccess: (response, policyId) => {
      if (!response.success) {
        toaster.create({
          type: 'error',
          title: 'Reenvio do pacote',
          description: response.error ?? 'Não foi possível reenviar o pacote.',
        });
        return;
      }
      toaster.create({
        type: 'success',
        title: 'Reenvio iniciado',
        description:
          response.data?.message ??
          'O pacote será enviado a Operações. Acompanhe pelo histórico.',
      });
      queryClient.invalidateQueries({
        queryKey: ['integration-details', policyId],
      });
    },
  });

  return {
    isVisible: isGroupLife,
    download: () => {
      if (control?.insuremoPolicyId) mutation.mutate(control.insuremoPolicyId);
    },
    isPending: mutation.isPending,
    canResend: isGroupLife && canManualEmission === true,
    resend: () => {
      if (!control?.insuremoPolicyId) return;
      if (
        !confirm(
          'Reenviar a Operações o aviso de emissão manual com o pacote de emissão? A proposta não é retransmitida.'
        )
      )
        return;
      resendMutation.mutate(control.insuremoPolicyId);
    },
    isResending: resendMutation.isPending,
  };
}
